<template>
    <div v-if="this.shouldBeVisible" class="birthday-voucher-popup-container">
        <modal
            ref="birthdayVoucherModal"
            id="birthdayVoucherPopup"
            size="modal-lg"
            :title="$t('order.birthdayVoucherPopupTitle', {
                amount: this.birthdayVoucherConfig.voucherAmount,
            })"
            :centered="true"
            :scrollable="true"
            :show="birthdayVoucherModal.show"
            :close="birthdayVoucherModal.hide"
            :closeable="birthdayVoucherModal.closeable"
            :tighter-close-button="true"
            @modal-opening="onModalOpening"
            @modal-close="onModalClosing"
        >
            <form
                method="post"
                action=""
                enctype="multipart/form-data"
                @submit="onFormSubmit($event)"
                class="birthday-voucher-popup-form"
                :style="formStyle"
            >
                <div ref="birthdayVoucherModalBody" class="modal-body" :style="bodyStyle">
                    <div
                        v-if="response.show && typeof response.message === 'string' && response.message.length > 0"
                    >
                        <div
                            class="alert"
                            :class="{
                                'alert-success': response.success,
                                'alert-danger': !response.success,
                            }"
                        >
                            <p class="mb-0">{{ response.message }}</p>
                        </div>
                    </div>
                    <div>
                        <div
                            class="mb-3"
                            v-html="$t('order.birthdayVoucherPopupText', {
                                amount: this.birthdayVoucherConfig.voucherAmount,
                            })"
                        ></div>
                        <div class="form-group">
                            <label class="col-form-label" for="birthday">
                                {{ $t('order.birthdayLabel') }}
                            </label>
                            <DatePicker
                                ref="birthdayDatepicker"
                                id="customer-birthday-date"
                                name="date"
                                required="required"
                                v-model="birthday"
                                :config="birthdayVoucherConfig.dpConfig"
                                :class="{ 'is-invalid': formErrors.date }"
                            ></DatePicker>
                            <div class="invalid-feedback">
                                <span
                                    class="d-block"
                                    v-for="error in formErrors.date"
                                    :key="error"
                                >{{ error }}</span
                                >
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="email">
                                {{ $t('shop.email') }}
                            </label>
                            <input
                                type="email"
                                name="email"
                                class="form-control"
                                id="email"
                                :placeholder="$t('shop.email_placeholder')"
                                :class="{ 'is-invalid': formErrors.email }"
                                v-model="email"
                                required
                            />
                            <div class="invalid-feedback">
                                <span class="d-block" v-for="error in formErrors.email" :key="error">{{
                                        error
                                    }}</span>
                            </div>
                        </div>

                        <div class="text-center mt-2">
                            <button
                                type="submit"
                                style="font-size: 1rem"
                                class="btn btn-primary btn-sm"
                                :disabled="runningRequest"
                            >
                                <i
                                    v-if="runningRequest"
                                    class="mdi mdi-loading mdi-spin"
                                    style="margin-right: 5px"
                                ></i>
                                <span
                                    v-html="$t('order.birthdayVoucherPopupSubmit')"
                                ></span>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
import DatePicker from '../DatePicker.vue';

export default {
    components: { DatePicker },
    props: {
        isPaymentConfirmed: {
            type: Boolean,
            required: true,
        },
        birthdayVoucherConfig: {
            type: Object,
            required: true,
            default() {
                return {
                    submitUri: null,
                    maxDate: null,
                    showPopup: false,
                };
            },
        },
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            email: this.order.email,
            birthday: null,
            birthdayVoucherModal: {
                show: false,
                hide: false,
                closeable: true,
            },
            formStyle: {
                height: '100%',
            },
            bodyStyle: {
                height: '100%',
            },
            formErrors: {},
            runningRequest: false,
            response: {
                show: false,
                success: false,
                message: null,
            },
        };
    },

    computed: {
        /**
         * Determine if the setup popup of order should be visible.
         *
         * @returns {Boolean}
         */
        shouldBeVisible() {
            return this.isPaymentConfirmed && this.birthdayVoucherConfig.showPopup;
        },
    },

    methods: {
        /**
         * Handle the visibility update of the modal.
         *
         * @returns {this}
         */
        showModal() {
            this.birthdayVoucherModal.show = true;
            this.birthdayVoucherModal.hide = false;
            this.birthdayVoucherModal.closeable = true;

            return this;
        },

        hideModal() {
            this.birthdayVoucherModal.show = false;
            this.birthdayVoucherModal.hide = true;
            this.birthdayVoucherModal.closeable = true;

            return this;
        },

        resetModal() {
            this.hideModal();
            this.runningRequest = false;
            this.response.show = false;
            this.response.success = false;
            this.response.message = null;
        },

        /**
         * Handle the event callback of opening modal.
         *
         * @returns {void}
         */
        onModalOpening() {
            this.$el.ownerDocument.body.classList.add('overflow-hidden');
        },

        /**
         * Handle the event callback for closing modal.
         *
         * @returns {void}
         */
        onModalClosing() {
            this.$el.ownerDocument.body.classList.remove('overflow-hidden');
            this.onExitSubmit();
            this.hideModal();
        },

        /**
         * @returns {void}
         */
        onExitSubmit() {
            // In the case the form was already submitted, we have to prevent another submit.
            if (this.runningRequest) {
                return;
            }

            // Update the is submitting flag and reset the response alert.
            this.runningRequest = true;

            axios.post(this.birthdayVoucherConfig.popUpUri).finally(() => {
                this.runningRequest = false;
            });
        },

        /**
         * Handle the submit callback of form to send the expected request.
         *
         * @param {SubmitEvent} $event
         *
         * @returns {void}
         */
        onFormSubmit($event) {
            $event.preventDefault();

            // In the case the form was already submitted, we have to prevent another submit.
            if (this.runningRequest) {
                return;
            }

            this.formErrors = {};

            if (
                typeof this.email !== 'string' ||
                this.email.trim() === '' ||
                (this.email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{1,63}$/g) || []).length === 0
            ) {
                this.formErrors = {
                    email: [this.$t('order.customerBirthdayPopupInvalidEmail')],
                };

                return;
            } else if (!this.birthday) {
                this.formErrors = {
                    date: [this.$t('order.customerBirthdayPopupMissingBirthday')],
                };

                return;
            }

            this.runningRequest = true;
            this.response.show = false;
            this.response.success = false;
            this.response.message = null;

            // Prepare the request
            const formData = new FormData();
            formData.append('email', this.email);
            formData.append('date', this.birthday);
            formData.append('locale', this.$language);
            formData.append('order', this.order.id);

            axios
                .post(this.birthdayVoucherConfig.createUri, formData)
                .then((response) => {
                    this.response.success = response.data.success;
                    this.response.message = response.data.message;

                    if (!this.response.success) {
                        if (typeof response.data.formErrors === 'object') {
                            for (const key in response.data.formErrors) {
                                if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                    delete response.data.formErrors[key];
                                }
                            }
                            this.formErrors = response.data.formErrors;
                        }

                        this.response.show = true;
                        this.runningRequest = false;

                        return;
                    }

                    this.response.show = true;
                    this.birthdayVoucherModal.closeable = false;

                    setTimeout(() => {
                        this.resetModal();
                    }, 2000);
                })
                .catch((error) => {
                    this.formErrors = {};

                    const errs = {};
                    if (error.response.data.errors) {
                        for (const [k, v] of Object.entries(error.response.data.errors)) {
                            if (k === 'order') {
                                this.response.success = false;
                                this.response.message = this.$t('order.customerBirthdayEntryFailed');
                                this.response.show = true;
                            } else {
                                Object.assign(errs, {[k]: v});
                            }
                        }
                        this.formErrors = errs;
                    }

                    this.runningRequest = false;
                });
        },
    },

    mounted() {
        if (this.shouldBeVisible) {
            this.showModal();
        } else {
            this.hideModal();
        }
    },
};
</script>
<style lang="scss">
.birthday-voucher-popup-container {
    @media screen and (max-width: 767px) {
        .modal-dialog {
            min-height: unset;
            max-height: unset;
            margin: 0;

            .modal-content {
                width: calc(100vw - 0.5rem);
            }
        }
    }
    @media screen and (min-width: 768px) {
        .horizontal-centered {
            margin: 0 auto;
        }
    }

    .modal-header {
        padding-bottom: 0;
    }
    .modal-body {
        padding-top: 0.5rem;
    }

    .modal-body .datepicker-input input {
        background: var(--main-background-light-light) !important;
    }
}
</style>
